import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, TrendingUp, FileText, ArrowRight, Loader2 } from "lucide-react";
import { API_BASE_URL } from "../config/api";
import { TOSS_LOGO_URL, BRAND } from "@/lib/branding";

const API = API_BASE_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [aRes, mRes] = await Promise.all([
        axios.get(`${API}/assessments`),
        axios.get(`${API}/materials`)
      ]);
      setAssessments(aRes.data);
      setSeeded(mRes.data.length > 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/seed`);
      await axios.post(`${API}/seed-sample`);
      setSeeded(true);
      await loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setSeeding(false);
    }
  };

  const mixedMethods = new Set(assessments.map(a=>a.results?.methodology_version || "legacy")).size > 1;
  const avgUpsi = assessments.length > 0
    ? (assessments.reduce((s, a) => s + (a.results?.upsi || 0), 0) / assessments.length).toFixed(2)
    : "N/A";

  const getScoreColor = (score) => {
    if (score >= 7) return "text-[#388E3C]";
    if (score >= 4) return "text-[#F57F17]";
    return "text-[#D32F2F]";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#43A047]" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl" data-testid="dashboard">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <img src={TOSS_LOGO_URL} alt={BRAND.short} className="h-12 w-auto object-contain" data-testid="dashboard-logo" />
          <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>
            Dashboard
          </h1>
        </div>
        <p className="text-base text-[#617364] mt-2">
          {BRAND.full} · Advanced Mode
        </p>
      </div>

      {!seeded && (
        <Card className="mb-8 border-[#E0E6E1] bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>
                  Initialize Benchmark Data
                </h3>
                <p className="text-sm text-[#617364] mt-1">
                  Load material benchmarks, product types, and sample assessment data
                </p>
              </div>
              <Button
                onClick={handleSeed}
                disabled={seeding}
                className="bg-[#43A047] hover:bg-[#388E3C] text-white"
                data-testid="seed-data-btn"
              >
                {seeding ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Seeding...</> : "Seed Data"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-[#E0E6E1] bg-white hover:shadow-sm transition-all">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] font-bold text-[#617364]">
              Total Assessments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }} data-testid="total-assessments">
              {assessments.length}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#E0E6E1] bg-white hover:shadow-sm transition-all">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] font-bold text-[#617364]">
              Average TOSS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-3xl font-light ${assessments.length > 0 ? getScoreColor(parseFloat(avgUpsi)) : 'text-[#1B291D]'}`}
               style={{ fontFamily: 'Work Sans' }} data-testid="avg-upsi">
              {mixedMethods ? "Mixed methods" : assessments.length ? (Number(avgUpsi) * 10).toFixed(2) : "N/A"}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#E0E6E1] bg-white hover:shadow-sm transition-all">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.2em] font-bold text-[#617364]">
              Quick Action
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/advanced/new")}
              className="bg-[#43A047] hover:bg-[#388E3C] text-white w-full"
              data-testid="new-assessment-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              New Assessment
            </Button>
          </CardContent>
        </Card>
      </div>

      {assessments.length > 0 && (
        <Card className="border-[#E0E6E1] bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>
                Recent Assessments
              </CardTitle>
              <Button variant="ghost" onClick={() => navigate("/advanced/assessments")} className="text-[#43A047] text-sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assessments.slice(0, 5).map((a) => (
                <div
                  key={a.id}
                  onClick={() => navigate(`/advanced/assessments/${a.id}`)}
                  className="flex items-center justify-between p-3 rounded-lg border border-[#E0E6E1] hover:bg-[#F8F9F8] cursor-pointer transition-all"
                  data-testid={`assessment-item-${a.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-[#617364]" />
                    <div>
                      <p className="text-sm font-medium text-[#1B291D]">{a.product_name}</p><p className="text-xs text-slate-500">{a.results?.aggregation?.method === "process_b" ? "Process B" : "Earlier method"}</p>
                      <p className="text-xs text-[#617364]">
                        {new Date(a.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="border-[#E0E6E1] text-[#617364] text-xs">
                      {a.product_type}
                    </Badge>
                    {a.results?.upsi != null && (
                      <span className={`text-lg font-medium ${getScoreColor(a.results.upsi)}`} style={{ fontFamily: 'Work Sans' }}>
                        {(a.results.upsi * 10).toFixed(2)}
                      </span>
                    )}
                    <TrendingUp className="w-4 h-4 text-[#617364]" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
